import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useState} from 'react';
import {ChangeDurationSelector} from '@Components/poster-editor/components/poster-seekbar/components/change-duration-selector';
import {CustomDurationItem} from '@Components/poster-editor/components/poster-seekbar/components/custom-duration-item';
import {SelectorType} from '@Components/poster-editor/components/poster-seekbar/components/change-duration-selector/change-duration-selector.types';
import {getCurrentPageDuration, getNumberOfItemsInCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import {getSelectorTypeOnOpen} from '@Components/poster-editor/components/poster-seekbar/poster-seekbar.helper';
import {useAppSelector} from '@/hooks';

interface SeekbarDurationOptionsContentProps {
  isMobileVersion?: boolean;
  onChangeSelectorTypeCallback?(type: SelectorType): void;
}

export function SeekbarDurationOptionsContent({isMobileVersion = false, onChangeSelectorTypeCallback = (): void => {}}: SeekbarDurationOptionsContentProps): ReactElement<any> | null {
  const [selectedSelectorType, setSelectedSelectorType] = useState<SelectorType>();
  const getNumOfItems = useMemo(() => {
    return getNumberOfItemsInCurrentPage;
  }, []);
  const numOfItems = useAppSelector((state) => {
    return getNumOfItems(state);
  });
  const currentPageDuration = useAppSelector(getCurrentPageDuration);

  useEffect((): void => {
    const selectorType = getSelectorTypeOnOpen();
    if (selectedSelectorType !== selectorType) {
      setSelectedSelectorType(selectorType);
      onChangeSelectorTypeCallback(selectorType);
    }
  }, [numOfItems]);

  const isCustomDurationSelected = (): boolean => {
    return selectedSelectorType === SelectorType.CUSTOM;
  };

  const changeDuration = async (type: SelectorType): Promise<void> => {
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    let duration;

    switch (type) {
      case SelectorType.VIDEO:
        duration = page?.items.getLongestVideoItemDuration();
        break;

      case SelectorType.SLIDESHOW:
        duration = page?.items.getLongestSlideshowItemDuration();
        break;

      case SelectorType.AUDIO:
        duration = window.posterEditor?.whiteboard?.audioClips.getDuration();
        break;

      default:
        duration = null;
        break;
    }

    if (duration) {
      await window.posterEditor?.whiteboard?.getCurrentPage()?.updateFromObject({
        duration,
      });
      await window.posterEditor?.whiteboard?.audioClips.trimAudiosExceedingPageDuration();
      await window.posterEditor?.whiteboard?.getCurrentPage().items.trimAllTranscriptItemsExceedingPageDuration();
    }
  };

  const onChangeSelectorType = (id: SelectorType): void => {
    if (selectedSelectorType !== id) {
      setSelectedSelectorType(id);
      onChangeSelectorTypeCallback(id);
      void changeDuration(id);
    }
  };

  const updateDurationAndTrimAudiosIfRequired = async (value: number, undoable = false): Promise<void> => {
    await window.posterEditor?.whiteboard?.getCurrentPage()?.updateFromObject(
      {
        duration: value,
      },
      {
        undoable,
      }
    );

    if (undoable) {
      await window.posterEditor?.whiteboard?.audioClips.trimAudiosExceedingPageDuration();
      await window.posterEditor?.whiteboard?.getCurrentPage().items.trimAllTranscriptItemsExceedingPageDuration();
    }
  };

  const updateDuration = (value: number, undoable = false): void => {
    if (!isCustomDurationSelected()) {
      onChangeSelectorType(SelectorType.CUSTOM);
    }
    void updateDurationAndTrimAudiosIfRequired(value, undoable);
  };

  const getTotalDisplayingOptions = (): number => {
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    let totalOptionsDisplaying = 0;
    if (page?.items.hasVideoItem()) {
      totalOptionsDisplaying += 1;
    }
    if (window.posterEditor?.whiteboard?.audioClips.hasAudio()) {
      totalOptionsDisplaying += 1;
    }
    if (page?.items.hasStreamingSlideShowItem()) {
      totalOptionsDisplaying += 1;
    }

    return totalOptionsDisplaying;
  };

  return (
    <>
      <ChangeDurationSelector onSelectType={onChangeSelectorType} selectedType={selectedSelectorType} />
      <CustomDurationItem
        isSelected={isCustomDurationSelected()}
        updateDuration={updateDuration}
        currentPageDuration={currentPageDuration}
        onChange={(): void => {
          onChangeSelectorType(SelectorType.CUSTOM);
        }}
        dontShowSelectedStateIfSelected={getTotalDisplayingOptions() === 0}
        isMobileVersion={isMobileVersion}
      />
    </>
  );
}
